import { c as _c } from "react/compiler-runtime";
import type { AgentTool, TaskCatalogEntry } from '@onefootprint/request-types/dashboard';
import { Badge, Button, Card, LinkButton, Toggle } from '@onefootprint/ui';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { VendorLogo } from '@/modules/agent';
import type { CatalogRequirements } from './catalog-requirements';
type CatalogConfirmationProps = {
  entry: TaskCatalogEntry;
  requirements: CatalogRequirements;
  /** The input schema has an unsaved or invalid edit, which the next save would keep over the inputs this adds. */
  inputsLocked?: boolean;
  /** The tools have an unsaved change, which the next save would keep over the ones this enables. */
  toolsLocked?: boolean;
  /** Input names and tool ids to start switched off. */
  initiallyOff?: {
    inputs?: string[];
    tools?: string[];
  };
  disabled?: boolean;
  onConfirm: (args: {
    addInputs: string[];
    addTools: AgentTool[];
  }) => void;
};
const SmallBadge = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Badge variant="neutral" className="px-1.5 py-0.5 text-caption-3">{children}</Badge>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
type RequirementRowProps = {
  toggleLabel: string;
  checked: boolean;
  toggleDisabled: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
};
const RequirementRow = t0 => {
  const $ = _c(10);
  const {
    toggleLabel,
    checked,
    toggleDisabled,
    onChange,
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="flex min-w-0 items-center gap-2">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== checked || $[3] !== onChange || $[4] !== toggleDisabled || $[5] !== toggleLabel) {
    t2 = <Toggle aria-label={toggleLabel} checked={checked} disabled={toggleDisabled} onChange={onChange} size="compact" />;
    $[2] = checked;
    $[3] = onChange;
    $[4] = toggleDisabled;
    $[5] = toggleLabel;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <Card.Body as="li" className="flex items-center justify-between gap-3 px-4 py-3">{t1}{t2}</Card.Body>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
type HeaderActionsProps = {
  added: number;
  total: number;
  selectable: string[];
  onToggleAll: (isOn: boolean) => void;
};
const HeaderActions = t0 => {
  const $ = _c(10);
  const {
    added,
    total,
    selectable,
    onToggleAll
  } = t0;
  if (total < 2) {
    return null;
  }
  const isAllOn = added === selectable.length;
  let t1;
  if ($[0] !== added || $[1] !== total) {
    t1 = <span className="text-body-3 text-tertiary">{added} of {total}</span>;
    $[0] = added;
    $[1] = total;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== isAllOn || $[4] !== onToggleAll || $[5] !== selectable.length) {
    t2 = selectable.length > 1 && <LinkButton variant="label-3" className="rounded-sm outline-none focus-visible:ring-1 focus-visible:ring-accent" onClick={() => onToggleAll(!isAllOn)}>{isAllOn ? "Deselect all" : "Select all"}</LinkButton>;
    $[3] = isAllOn;
    $[4] = onToggleAll;
    $[5] = selectable.length;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="flex items-center gap-3">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
const useSwitchedOff = t0 => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initial = t1;
  let t2;
  if ($[2] !== initial) {
    t2 = () => new Set(initial);
    $[2] = initial;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [off, setOff] = useState(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (ids, on) => setOff(current => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      return next;
    });
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const update = t3;
  let t4;
  if ($[5] !== off) {
    t4 = id_0 => off.has(id_0);
    $[5] = off;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = id_1 => on_0 => update([id_1], on_0);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4) {
    t6 = {
      isOff: t4,
      set: t5,
      update
    };
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
const CatalogConfirmation = t0 => {
  const $ = _c(31);
  const {
    entry,
    requirements,
    inputsLocked,
    toolsLocked,
    initiallyOff,
    disabled,
    onConfirm
  } = t0;
  const {
    inputs,
    fromEarlierTasks,
    tools
  } = requirements;
  let t1;
  if ($[0] !== fromEarlierTasks) {
    t1 = fromEarlierTasks.map(_temp).join(", ");
    $[0] = fromEarlierTasks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const earlierOutputs = t1;
  const offInputs = useSwitchedOff(initiallyOff?.inputs);
  const offTools = useSwitchedOff(initiallyOff?.tools);
  const addedInputs = inputsLocked ? [] : inputs.filter(input => !offInputs.isOff(input.fieldName));
  const availableTools = tools.filter(_temp2);
  const addedTools = toolsLocked ? [] : availableTools.filter(tool_0 => !offTools.isOff(tool_0.id));
  const hasToolsLeftOut = !toolsLocked && addedTools.length < availableTools.length;
  let t2;
  if ($[2] !== disabled || $[3] !== inputs || $[4] !== inputsLocked) {
    t2 = inputsLocked || disabled ? [] : inputs.map(_temp3);
    $[2] = disabled;
    $[3] = inputs;
    $[4] = inputsLocked;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const selectableInputs = t2;
  const selectableTools = toolsLocked || disabled ? [] : availableTools.map(_temp4);
  const headingRef = useRef(null);
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => headingRef.current?.focus();
    t4 = [];
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const t5 = "flex flex-col gap-5 px-6 py-6";
  let t6;
  if ($[8] !== entry.name) {
    t6 = <h3 ref={headingRef} tabIndex={-1} className="text-label-2 text-primary outline-none">{entry.name}</h3>;
    $[8] = entry.name;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="text-body-3 text-secondary">This task needs a few things this agent doesn't have yet.</p>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6) {
    t8 = <div className="flex flex-col gap-1">{t6}{t7}</div>;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = "flex flex-col gap-4";
  const t10 = inputs.length > 0 && <section aria-label="Input schema" className="flex flex-col gap-2"><Card.Root><Card.Header title="Input schema" rightContent={<HeaderActions added={addedInputs.length} total={inputs.length} selectable={selectableInputs} onToggleAll={isOn => offInputs.update(selectableInputs, isOn)} />} /><Card.List>{inputs.map(input_1 => <RequirementRow key={input_1.fieldName} toggleLabel={`Add ${input_1.fieldName}`} checked={addedInputs.includes(input_1)} toggleDisabled={!!(inputsLocked || disabled)} onChange={offInputs.set(input_1.fieldName)}><code className="truncate text-snippet-3 text-primary">{input_1.fieldName}</code>{input_1.declaredOptional && <SmallBadge>Make required</SmallBadge>}{input_1.declaredNotDocument && <SmallBadge>Make a document</SmallBadge>}{!input_1.isRequired && <SmallBadge>Optional</SmallBadge>}</RequirementRow>)}</Card.List></Card.Root>{inputsLocked ? <p className="text-body-3 text-error">Save the input schema, or fix its errors, before adding inputs.</p> : addedInputs.length < inputs.length && <p className="text-body-3 text-tertiary">The task reads the inputs left off. Add them later in Input schema.</p>}</section>;
  const t11 = tools.length > 0 && <section aria-label="Tools" className="flex flex-col gap-2"><Card.Root><Card.Header title="Tools" rightContent={<HeaderActions added={addedTools.length} total={tools.length} selectable={selectableTools} onToggleAll={isOn_0 => offTools.update(selectableTools, isOn_0)} />} /><Card.List>{tools.map(tool_2 => <RequirementRow key={tool_2.id} toggleLabel={`Enable ${tool_2.name}`} checked={addedTools.includes(tool_2)} toggleDisabled={!!(tool_2.unavailableReason || disabled || toolsLocked)} onChange={offTools.set(tool_2.id)}><VendorLogo name={tool_2.name} toolKind={tool_2.id} /><span className="flex min-w-0 flex-col"><span className="text-body-3 text-primary">{tool_2.name}</span>{tool_2.unavailableReason && <span className="text-body-3 text-error">Can't be enabled: {tool_2.unavailableReason}</span>}</span></RequirementRow>)}</Card.List></Card.Root>{toolsLocked && <p className="text-body-3 text-error">Save the tools before enabling more.</p>}{hasToolsLeftOut && <p className="text-body-3 text-tertiary">The task calls the tools left off. Enable them later in Tools.</p>}</section>;
  let t12;
  if ($[13] !== earlierOutputs || $[14] !== fromEarlierTasks.length) {
    t12 = fromEarlierTasks.length > 0 && <p className="text-body-3 text-tertiary">{fromEarlierTasks.length === 1 ? `Reads ${earlierOutputs} from an earlier task, so it stays out of the input schema.` : `Reads ${earlierOutputs} from an earlier task, so they stay out of the input schema.`}</p>;
    $[13] = earlierOutputs;
    $[14] = fromEarlierTasks.length;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t12) {
    t13 = <div className={t9}>{t10}{t11}{t12}</div>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== addedInputs || $[21] !== addedTools || $[22] !== onConfirm) {
    t14 = () => onConfirm({
      addInputs: addedInputs.map(_temp5),
      addTools: addedTools.map(_temp6)
    });
    $[20] = addedInputs;
    $[21] = addedTools;
    $[22] = onConfirm;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== disabled || $[25] !== t14) {
    t15 = <div className="flex justify-end"><Button variant="primary" disabled={disabled} onClick={t14}>Add task</Button></div>;
    $[24] = disabled;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t13 || $[28] !== t15 || $[29] !== t8) {
    t16 = <div className={t5}>{t8}{t13}{t15}</div>;
    $[27] = t13;
    $[28] = t15;
    $[29] = t8;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
};
export default CatalogConfirmation;
function _temp(key) {
  return `outputs.${key}`;
}
function _temp2(tool) {
  return !tool.unavailableReason;
}
function _temp3(input_0) {
  return input_0.fieldName;
}
function _temp4(tool_1) {
  return tool_1.id;
}
function _temp5(input_2) {
  return input_2.fieldName;
}
function _temp6(tool_3) {
  return tool_3.tool;
}